import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface ImageCellProps<T> {
  /** The item to display/edit the image for */
  item?: T;
  /** The endpoint to use for updating (e.g., '/tours', '/hotels') */
  endpoint: string;
  /** The entity name for query invalidation (e.g., 'Tour', 'Hotel') */
  entityName: string;
  /** The label for the upload modal (e.g., 'Tour Image', 'Hotel Image') */
  imageLabel: string;
  /** Function to extract the featured image from the item */
  getFeaturedImage: (item: T) => { id: string; name?: string; url?: string } | undefined;
  /** Function to extract the item ID */
  getItemId: (item: T) => string;
  /** Size variant: 'sm' for list pages (64x64), 'lg' for detail pages (256x192) */
  size?: 'sm' | 'lg';
}

/**
 * Generic editable image cell component with upload and delete actions
 * Can be used for any entity type (tours, hotels, greater areas, etc.)
 */
export function ImageCell<T>({
  item,
  endpoint,
  entityName,
  imageLabel,
  getFeaturedImage,
  getItemId,
  size = 'sm',
}: ImageCellProps<T>) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newMediaId, setNewMediaId] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!item) return null;

  // Size classes
  const sizeClasses = {
    container: size === 'lg' ? 'w-64 h-48' : 'w-16 h-16',
    icon: size === 'lg' ? 'h-8 w-8' : 'h-6 w-6',
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setUploadModalOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setDeleteModalOpen(true);
  };

  const handleUploadSubmit = async () => {
    if (!newMediaId) return;

    setIsUploading(true);
    try {
      const itemId = getItemId(item);
      await apiClient.put(`${endpoint}/${itemId}`, {
        featuredImageId: newMediaId,
      });
      toast.success('Featured image added');
      setUploadModalOpen(false);
      setNewMediaId(undefined);
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: [entityName] });
    } catch (error) {
      toast.error('Failed to add image');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const itemId = getItemId(item);
      await apiClient.put(`${endpoint}/${itemId}`, {
        featuredImageId: null,
      });
      toast.success('Featured image removed');
      setDeleteModalOpen(false);
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: [entityName] });
    } catch (error) {
      toast.error('Failed to remove image');
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const featuredImage = getFeaturedImage(item);
  const imageUrl =
    featuredImage?.url || (featuredImage?.id ? `/api/v1/media/${featuredImage.id}` : null);

  if (!imageUrl) {
    return (
      <>
        <div
          className={`relative group ${sizeClasses.container} bg-primary/10 rounded flex items-center justify-center cursor-pointer hover:bg-primary/20 transition-colors`}
          onClick={handleUploadClick}
        >
          <ImageIcon className={`${sizeClasses.icon} text-primary`} />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
            <Plus className={`${sizeClasses.icon} text-white`} />
          </div>
        </div>

        {/* Upload Modal */}
        <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Add Featured Image</DialogTitle>
              <DialogDescription>Upload an image for this {entityName.toLowerCase()}</DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <ImageUploadDropzone
                value={newMediaId}
                onChange={setNewMediaId}
                label={imageLabel}
                required
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUploadSubmit} disabled={!newMediaId || isUploading}>
                {isUploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Image
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className={`relative group ${sizeClasses.container}`}>
        <img
          src={imageUrl}
          alt={featuredImage?.name || `${entityName} image`}
          className="w-full h-full object-cover rounded"
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="h-7 w-7 p-0"
            title="Remove image"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Remove Featured Image</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this image from the {entityName.toLowerCase()}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
